import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["countrySelect", "numberInput", "e164Input"]
  static values = {
    defaultCountry: String,
    placeholder: String
  }

  connect() {
    this.initializePhoneInput()
  }

  initializePhoneInput() {
    // Set default country if provided
    if (this.hasDefaultCountryValue && this.countrySelectTarget.value === "") {
      this.countrySelectTarget.value = this.defaultCountryValue
    }

    // Set placeholder if provided
    if (this.hasPlaceholderValue && this.numberInputTarget.placeholder === "") {
      this.numberInputTarget.placeholder = this.placeholderValue
    }

    // Initialize with current values
    this.updateE164()
  }

  // Update E.164 when country or number changes
  updateE164() {
    const countryCode = this.countrySelectTarget.value
    const phoneNumber = this.numberInputTarget.value.replace(/\D/g, '')

    if (countryCode && phoneNumber) {
      const e164Number = `+${countryCode}${phoneNumber}`
      this.e164InputTarget.value = e164Number
    } else {
      this.e164InputTarget.value = ''
    }
  }

  // Format display number when input loses focus
  formatNumber() {
    const countryCode = this.countrySelectTarget.value
    const phoneNumber = this.numberInputTarget.value.replace(/\D/g, '')

    if (countryCode && phoneNumber) {
      let formatted = phoneNumber

      // Format based on country code
      switch (countryCode) {
        case "1": // US/Canada
          if (phoneNumber.length === 10) {
            formatted = `(${phoneNumber.slice(0,3)}) ${phoneNumber.slice(3,6)}-${phoneNumber.slice(6)}`
          }
          break
        case "44": // UK
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "49": // Germany
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{2,4})(\d{3,4})(\d{4})/, '$1 $2 $3')
          }
          break
        case "33": // France
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')
          }
          break
        case "39": // Italy
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "34": // Spain
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "31": // Netherlands
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "32": // Belgium
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "41": // Switzerland
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "43": // Austria
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{1,4})(\d{3,4})(\d{4})/, '$1 $2 $3')
          }
          break
        case "46": // Sweden
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "47": // Norway
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{3})(\d{2})(\d{3})/, '$1 $2 $3')
          }
          break
        case "45": // Denmark
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')
          }
          break
        case "358": // Finland
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "48": // Poland
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4')
          }
          break
        case "420": // Czech Republic
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "36": // Hungary
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})(\d{2})/, '$1 $2 $3 $4')
          }
          break
        case "40": // Romania
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{2,3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "359": // Bulgaria
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{2,3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "385": // Croatia
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "421": // Slovakia
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "386": // Slovenia
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "370": // Lithuania
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{3})(\d{2})(\d{3})/, '$1 $2 $3')
          }
          break
        case "371": // Latvia
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "372": // Estonia
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{2,3})(\d{2,3})(\d{2,3})/, '$1 $2 $3')
          }
          break
        case "353": // Ireland
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "351": // Portugal
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{2,3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "30": // Greece
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{2,4})(\d{3,4})(\d{4})/, '$1 $2 $3')
          }
          break
        case "357": // Cyprus
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "356": // Malta
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "352": // Luxembourg
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{2,3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "354": // Iceland
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "61": // Australia
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{4})(\d{4})/, '$1 $2 $3')
          }
          break
        case "64": // New Zealand
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "81": // Japan
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,3})(\d{3,4})(\d{4})/, '$1 $2 $3')
          }
          break
        case "82": // South Korea
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3,4})(\d{4})/, '$1 $2 $3')
          }
          break
        case "86": // China
          if (phoneNumber.length >= 11) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})(\d{4})/, '$1 $2 $3')
          }
          break
        case "91": // India
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{2,5})(\d{3,5})(\d{4})/, '$1 $2 $3')
          }
          break
        case "55": // Brazil
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{2})(\d{4,5})(\d{4})/, '$1 $2 $3')
          }
          break
        case "52": // Mexico
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{2,3})(\d{4})(\d{4})/, '$1 $2 $3')
          }
          break
        case "54": // Argentina
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{2,4})(\d{3,4})(\d{4})/, '$1 $2 $3')
          }
          break
        case "56": // Chile
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "57": // Colombia
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{1,3})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "51": // Peru
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "58": // Venezuela
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{2,3})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "27": // South Africa
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "20": // Egypt
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "234": // Nigeria
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "254": // Kenya
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "256": // Uganda
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "255": // Tanzania
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "251": // Ethiopia
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "212": // Morocco
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "216": // Tunisia
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "213": // Algeria
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "966": // Saudi Arabia
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "971": // UAE
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "974": // Qatar
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "965": // Kuwait
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "973": // Bahrain
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "968": // Oman
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "962": // Jordan
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "961": // Lebanon
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "963": // Syria
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "964": // Iraq
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{1,3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "98": // Iran
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "92": // Pakistan
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{1,3})(\d{3})(\d{4})/, '$1 $2 $3')
          }
          break
        case "880": // Bangladesh
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "94": // Sri Lanka
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "60": // Malaysia
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "65": // Singapore
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "66": // Thailand
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "84": // Vietnam
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "63": // Philippines
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "62": // Indonesia
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,3})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "95": // Myanmar
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "855": // Cambodia
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "856": // Laos
          if (phoneNumber.length >= 10) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "673": // Brunei
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "670": // East Timor
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "675": // Papua New Guinea
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "679": // Fiji
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "689": // French Polynesia
          if (phoneNumber.length >= 6) {
            formatted = phoneNumber.replace(/(\d{2})(\d{2})(\d{2})/, '$1 $2 $3')
          }
          break
        case "687": // New Caledonia
          if (phoneNumber.length >= 6) {
            formatted = phoneNumber.replace(/(\d{2})(\d{2})(\d{2})/, '$1 $2 $3')
          }
          break
        case "678": // Vanuatu
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "677": // Solomon Islands
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "686": // Kiribati
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "688": // Tuvalu
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "674": // Nauru
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "680": // Palau
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "691": // Micronesia
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "692": // Marshall Islands
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "269": // Comoros
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "253": // Djibouti
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "252": // Somalia
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "291": // Eritrea
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        case "211": // South Sudan
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "236": // Central African Republic
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "235": // Chad
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "237": // Cameroon
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "240": // Equatorial Guinea
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "241": // Gabon
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "242": // Republic of the Congo
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "243": // Democratic Republic of the Congo
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "244": // Angola
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "263": // Zimbabwe
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "260": // Zambia
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "265": // Malawi
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "258": // Mozambique
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "267": // Botswana
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "264": // Namibia
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "266": // Lesotho
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "268": // Eswatini
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "261": // Madagascar
          if (phoneNumber.length >= 9) {
            formatted = phoneNumber.replace(/(\d{1,2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "230": // Mauritius
          if (phoneNumber.length >= 8) {
            formatted = phoneNumber.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3')
          }
          break
        case "248": // Seychelles
          if (phoneNumber.length >= 7) {
            formatted = phoneNumber.replace(/(\d{3})(\d{4})/, '$1 $2')
          }
          break
        default:
          // For other countries, just add spaces every 3-4 digits
          if (phoneNumber.length >= 6) {
            formatted = phoneNumber.replace(/(\d{2,4})(\d{3,4})(\d{3,4})/, '$1 $2 $3')
          }
      }

      this.numberInputTarget.value = formatted
    }
  }

  // Strip formatting when input gains focus
  stripFormatting() {
    this.numberInputTarget.value = this.numberInputTarget.value.replace(/\D/g, '')
  }

  // Event handlers
  countryChanged() {
    this.updateE164()
  }

  numberChanged() {
    this.updateE164()
  }

  numberBlurred() {
    this.formatNumber()
  }

  numberFocused() {
    this.stripFormatting()
  }
}
